export enum ShipmentStatus {
  PENDING = 'pending',
  OUT_FOR_PICKUP = 'out_for_pickup',
  COLLECTED = 'collected',
  IN_TRANSIT = 'in_transit',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  RETURNING = 'returning',
  RETURNED = 'returned',
}

export interface Address {
  street: string;
  neighborhood: string;
  city: string;
  lat: number;
  lng: number;
}

export interface SenderInfo {
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  alternative_phone?: string | null;
  store_name?: string | null;
}

export interface RecipientInfo {
  name: string;
  phone: string;
  email: string | null;
  notes: string | null;
}

export interface ShipmentItem {
  id: number;
  name: string;
  quantity: number;
  sku: string | null;
}

export interface ShipmentListItem {
  id: string;
  tracking_number: string;
  recipient_name: string;
  recipient_phone: string;
  address: string;
  // The list endpoint returns a status object (code + localized name + color),
  // not a flat string. Null when the shipment has no resolved status row.
  status: {
    code: string;
    color: string;
    name_: string;
  } | null;
  shipment_value: string;
  total_amount: number;
  service_type: string;
  created_at: string;
}

export interface Shipment {
  id: string;
  tracking_number: string;

  // API returns a status object, not a flat status_code
  status: {
    code: ShipmentStatus;
    color: string;
    name_: string;
  };

  from_address: Address;
  to_address: Address;

  // Snapshot of sender contact info captured at shipment creation
  sender_info?: SenderInfo;
  recipient_info: RecipientInfo;
  items: ShipmentItem[];

  // Parties
  tenant?: {
    id: string;
    name: string;
    phone: string;
    email?: string | null;
    address: string | null;
  };
  sender_warehouse?: {
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
  } | null;
  carrier?: {
    id: string;
    name: string;
    logo_url: string;
    contact_info: string;
  };
  driver?: {
    id: string;
    name: string;
    phone: string;
  };

  // Financials — API field names
  shipment_value: number;
  cod_amount: number;
  actual_payment_type?: string;   // API name (was: payment_type)
  payment_status: string;
  delivery_fee?: number;          // API name (was: delivery_fees)
  platform_fee?: number;
  tenant_payout?: number;
  total_amount?: number;          // API name (was: total_value)

  notes?: string | null;
  created_at: string;
  updated_at?: string;
  delivered_at?: string;
  delivery_date?: string;
  scheduled_at?: string;

  // Location extras
  from_city?: { id: string; code: string; name: string } | null;
  to_city?: { id: string; code: string; name: string } | null;
  from_neighborhood?: { id: string; name: string } | null;
  to_neighborhood?: { id: string; name: string } | null;
  from_address_verified?: boolean;
  to_address_verified?: boolean;

  available_actions?: Array<{ code: string; name: string; color: string }>;
  driver_settled?: boolean;
  tenant_settled?: boolean;

  // Trip/routing metadata — direct shipments have total_trips === 1
  total_trips?: number;

  service_type: { id: string; name: string };
}

export interface ShipmentFilters {
  search?: string;
  status?: string;
  page?: number;
  limit?: number;
}
